import { ONBOARDING_FORM_STYLES } from "@/features/onboarding/components/onboarding-form-styles";
import { OnboardingStepHeader } from "@/features/onboarding/components/onboarding-step-header";
import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import Loading from "@/components/Loading";
import { useTRPC } from "@/trpc";
import { useCredentials } from "@/features/credentials/hooks/useCredentials";
import { useProviderCredentialEditing } from "@/features/credentials/hooks/use-provider-credential-editing";
import { CredentialTypeBlock } from "@/features/credentials/components/credential-card/credential-type-block";
import type { Credential } from "@/features/credentials/types";
import { isReviewUploadBlocked } from "@/features/credentials/lib/review-upload-security";
import { missingOnboardingDocuments } from "../utils/onboarding-document-requirements";
import { CredentialUploadMissingDialog } from "./CredentialUploadMissingDialog";
import { OnboardingDocumentChecklist } from "./onboarding-document-checklist";

export function NativeCredentialUploadStep({
  userId,
  onComplete,
  onBack,
}: {
  userId: string;
  onComplete: () => Promise<void>;
  onBack?: () => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const credentials = useCredentials(userId);
  const editing = useProviderCredentialEditing({
    userId,
    data: credentials.data,
    uploadButtonClassName: `${ONBOARDING_FORM_STYLES.primaryButton} bg-primary hover:bg-primary/90 focus-visible:ring-ring`,
  });
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<{
    missingDocuments: { name: string }[];
    pendingReviewCount: number;
    blockedUploadCount: number;
  } | null>(null);

  async function finishUploading() {
    if (editing.isDialogOpen || checking) {
      return;
    }
    setChecking(true);
    setError(null);
    try {
      // Check fresh native holdings and review items together; an uploaded file
      // is not a held credential until the provider reviews and saves it.
      const [current, review] = await Promise.all([
        queryClient.fetchQuery(
          trpc.credentials.getMyCredentials.queryOptions(
            { switchUserId: userId },
            { staleTime: 0 },
          ),
        ),
        queryClient.fetchQuery(
          trpc.credentials.listMyCandidates.queryOptions(
            { switchUserId: userId },
            { staleTime: 0 },
          ),
        ),
      ]);
      const missingDocuments = missingOnboardingDocuments(current.credentials);
      if (missingDocuments.length > 0 || review.length > 0) {
        const blockedUploadCount = review.filter(isReviewUploadBlocked).length;
        setWarning({
          missingDocuments,
          pendingReviewCount: review.length - blockedUploadCount,
          blockedUploadCount,
        });
      } else {
        await onComplete();
      }
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Could not check your documents. Please try again.",
      );
    } finally {
      setChecking(false);
    }
  }

  if (!credentials.data) {
    if (credentials.error) {
      return (
        <div role="alert" className="space-y-3">
          <p>Could not load your credentials.</p>
          <Button
            variant="outline"
            onClick={() =>
              void queryClient.invalidateQueries({
                queryKey: trpc.credentials.getMyCredentials.queryKey({
                  switchUserId: userId,
                }),
              })
            }
          >
            Try again
          </Button>
        </div>
      );
    }
    return (
      <div role="status" className="flex items-center gap-2">
        <Loading className="size-5" />
        Loading credentials…
      </div>
    );
  }

  const heldGroups = new Map<string, Credential[]>();
  for (const credential of credentials.data.credentials) {
    if (!credential.present || credential.documents.length === 0) {
      continue;
    }
    const group = heldGroups.get(credential.credentialType);
    if (group) {
      group.push(credential);
    } else {
      heldGroups.set(credential.credentialType, [credential]);
    }
  }

  return (
    <div>
      {onBack && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          disabled={checking || editing.isDialogOpen}
          className={ONBOARDING_FORM_STYLES.backButton}
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Back
        </Button>
      )}
      <OnboardingStepHeader
        title="Upload Your Documents"
        description="Upload your credential documents, review the extracted details, and save them to your profile."
      />
      <div className="space-y-5">
        <OnboardingDocumentChecklist
          credentials={credentials.data.credentials}
        />
        {editing.summaryActions}
        {editing.notice}
        <section className={ONBOARDING_FORM_STYLES.section}>
          <h3 className={ONBOARDING_FORM_STYLES.sectionHeading}>
            Saved documents
          </h3>
          {heldGroups.size > 0 ? (
            [...heldGroups.values()].map((group) => (
              <CredentialTypeBlock
                key={group[0].credentialType}
                credentials={group}
                userId={userId}
                {...editing.cardActions}
              />
            ))
          ) : (
            <p className="mt-2 text-sm text-slate-500">
              Your documents will appear here after you review and save them.
            </p>
          )}
        </section>
        {error && (
          <p role="alert" className={ONBOARDING_FORM_STYLES.error}>
            {error}
          </p>
        )}
        <div className={ONBOARDING_FORM_STYLES.footer}>
          <Button
            size="none"
            className={ONBOARDING_FORM_STYLES.primaryButton}
            onClick={() => void finishUploading()}
            disabled={checking || editing.isDialogOpen}
          >
            {checking ? <Loading className="size-4" /> : "Finished Uploading"}
          </Button>
        </div>
      </div>
      {warning && (
        <CredentialUploadMissingDialog
          {...warning}
          onContinue={onComplete}
          onBack={() => setWarning(null)}
        />
      )}
      {editing.modals}
    </div>
  );
}
